import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Baby,
  CalendarOff,
  Clock,
  House,
  Mail,
  MapPin,
  Sprout,
  Users,
  type LucideIcon,
} from "lucide-react";
import Section from "../components/Section";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Seo from "../components/Seo";
import PhotoGallery from "../components/PhotoGallery";
import { getCrecheBySlug, isPlaceholder, libelleExterieur } from "../data/creches";
import { breadcrumbJsonLd, crecheJsonLd } from "../lib/jsonld";
import { site, PREINSCRIPTION_URL } from "../data/site";

export default function CrecheDetail() {
  const { slug } = useParams();
  const creche = slug ? getCrecheBySlug(slug) : undefined;

  if (!creche) {
    return (
      <>
        <Seo title="Crèche introuvable" path={`/micro-creches/${slug ?? ""}`} noindex />
        <Section title="Crèche introuvable" description="Le slug demandé n'existe pas.">
          <Link to="/nos-micro-creches">
            <Button variant="outline">Retour à la liste</Button>
          </Link>
        </Section>
      </>
    );
  }

  const fmtHeure = (t: string) => t.replace(":", "h").replace(/^0/, "");

  const infoLines: { icon: LucideIcon; text: string; sub?: string; award?: boolean; href?: string }[] = [
    {
      icon: Clock,
      text: `Du lundi au vendredi de ${fmtHeure(creche.horaires.ouverture)} à ${fmtHeure(creche.horaires.fermeture)}`,
    },
    {
      icon: CalendarOff,
      text: `Fermetures annuelles: ${creche.fermeturesAnnuelles.join(", ")}`,
    },
    {
      icon: House,
      text: `Superficie: ${creche.superficie}m²`,
      award: creche.superficieRecord,
    },
  ];
  if (creche.superficieJardin > 0) {
    infoLines.push({ icon: Sprout, text: `${libelleExterieur(creche)}: ${creche.superficieJardin}m²` });
  }
  infoLines.push(
    { icon: Users, text: `Capacité d'accueil: ${creche.capacite} enfants` },
    {
      icon: Baby,
      text: `Accueil des enfants: de ${creche.agesAccueillis}`,
    },
    { icon: MapPin, text: creche.adresse, sub: `${creche.codePostal} ${creche.ville}` },
  );
  if (!isPlaceholder(creche.email)) {
    infoLines.push({ icon: Mail, text: creche.email, href: `mailto:${creche.email}` });
  }
  const hasMot = !isPlaceholder(creche.motDirectrice.texte);
  const tones = [
    "bg-sage-soft text-sage-hover",
    "bg-honey-soft text-honey-hover",
    "bg-terra-soft text-terra-hover",
  ];

  return (
    <>
      <Seo
        path={`/micro-creches/${creche.slug}`}
        title={`${creche.nom} — Micro-crèche à ${creche.ville}`}
        description={`La La Land ${creche.nom} : ${creche.capacite} enfants, ${creche.superficie} m²${creche.superficieJardin > 0 ? ` + ${libelleExterieur(creche).toLowerCase()} de ${creche.superficieJardin} m²` : ""}. Pédagogie musicale. Accueil de ${creche.agesAccueillis}, du lundi au vendredi de ${creche.horaires.ouverture} à ${creche.horaires.fermeture}.`}
        image={creche.photoPrincipale}
        jsonLd={[
          crecheJsonLd(creche),
          breadcrumbJsonLd([
            { name: "Accueil", url: site.url },
            { name: "Nos micro-crèches", url: `${site.url}/nos-micro-creches` },
            { name: creche.nom, url: `${site.url}/micro-creches/${creche.slug}` },
          ]),
        ]}
      />
      <section className="pt-8 pb-6">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link
            to="/nos-micro-creches"
            className="text-sm text-ink-soft hover:text-terra"
          >
            ← Toutes nos crèches
          </Link>
          <h1 className="mt-6 font-display text-4xl md:text-6xl font-semibold tracking-tight">
            {creche.nom}
          </h1>
          <p className="mt-3 text-lg text-ink-soft inline-flex items-center gap-2">
            <MapPin size={18} strokeWidth={1.5} />
            {creche.adresse} · {creche.codePostal} {creche.ville}
          </p>
        </div>
      </section>

      {creche.photosDisponibles && creche.galerie.length > 0 ? (
        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 mb-4">
            <p className="text-xs uppercase tracking-wider font-semibold text-ink-muted">
              Galerie
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-1">
              Découvrez la crèche
            </h2>
          </div>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <PhotoGallery
              images={[creche.photoPrincipale, ...creche.galerie]}
              crecheNom={creche.nom}
            />
          </div>
        </section>
      ) : (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="aspect-[16/8] rounded-4xl bg-gradient-to-br from-sage-soft via-honey-soft to-terra-soft flex items-end p-6 shadow-soft-md">
              <Badge tone="neutral" className="bg-white/85 backdrop-blur">
                Photo hero · à fournir
              </Badge>
            </div>
          </div>
        </section>
      )}

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-5 gap-6">
            <div
              className={`${hasMot ? "lg:col-span-2" : "lg:col-span-5"} bg-surface border border-edge-soft rounded-3xl p-6 md:p-8 shadow-soft-sm`}
            >
              <p className="text-xs uppercase tracking-wider font-semibold text-ink-muted mb-5">
                Informations pratiques
              </p>
              <ul
                className={`grid gap-x-8 gap-y-5 ${hasMot ? "" : "sm:grid-cols-2 lg:grid-cols-3"}`}
              >
                {infoLines.map((line, i) => {
                  const Icon = line.icon;
                  const body = (
                    <>
                      <span
                        className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${tones[i % tones.length]}`}
                        aria-hidden="true"
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.8} />
                      </span>
                      <span className="font-display text-base leading-snug break-words pt-2">
                        {line.text}
                        {line.award && (
                          <Award
                            className="inline w-4 h-4 ml-1 -mt-0.5 text-honey-hover"
                            strokeWidth={2}
                            aria-label="Record"
                          />
                        )}
                        {line.sub && <span className="block">{line.sub}</span>}
                      </span>
                    </>
                  );
                  return (
                    <li key={i}>
                      {line.href ? (
                        <a
                          href={line.href}
                          className="flex items-start gap-3 hover:text-terra transition-colors"
                        >
                          {body}
                        </a>
                      ) : (
                        <div className="flex items-start gap-3">{body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {hasMot ? (
              <blockquote className="lg:col-span-3 bg-muted rounded-3xl p-6 md:p-8 border border-edge-soft relative">
                <p className="text-xs uppercase tracking-wider font-semibold text-ink-muted mb-3">
                  Mot de la directrice
                </p>
                <span className="font-display text-6xl text-terra/30 absolute top-4 right-6 leading-none">
                  “
                </span>
                <div className="font-display text-base md:text-lg leading-relaxed space-y-3">
                  {creche.motDirectrice.texte
                    .split(/\n\n+/)
                    .map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                </div>
                <footer className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sage-soft text-sage-hover grid place-items-center font-semibold text-sm">
                    {creche.motDirectrice.auteur.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{creche.motDirectrice.auteur}</p>
                    <p className="text-xs text-ink-muted">Directrice</p>
                  </div>
                </footer>
              </blockquote>
            ) : null}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-terra to-terra-hover text-white rounded-4xl p-8 md:p-14 shadow-soft-lg flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="font-display text-2xl md:text-3xl font-semibold">
                Une place pour votre enfant chez {creche.nom} ?
              </p>
              <p className="opacity-90 mt-1">
                Pré-inscription en quelques minutes.
              </p>
            </div>
            <a
              href={PREINSCRIPTION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-terra-hover px-6 py-3.5 rounded-xl font-medium shadow-soft-md hover:bg-cream transition active:scale-[0.97]"
            >
              Pré-inscription
              <ArrowRight size={18} strokeWidth={2} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
